import { createSSEConnection } from './sse';
import { getToken } from '@/utils/auth';
import store from '@/store';

class SSEManager {
  constructor() {
    this.sseConnection = null;
    this.isConnected = false;
    this.messageHandlers = new Map();
    this.reconnectTimer = null;
    this.maxReconnectAttempts = 5;
    this.reconnectAttempts = 0;
    this.reconnectInterval = 2000;
    this.updateCountTimer = null; // 用于防抖更新未读数量
    this.updateCountDelay = 1000; // 1秒防抖延迟
    this.socketMessageHandler = null; // socket 消息处理器
  }

  /**
   * 初始化SSE连接
   */
  init() {
    return;
    if (this.isConnected || this.sseConnection) {
      console.log('SSE连接已存在，跳过初始化');
      return;
    }

    const token = getToken();
    if (!token) {
      console.log('未找到认证token，跳过SSE初始化');
      return;
    }

    this.createConnection();
  }

  /**
   * 创建SSE连接
   */
  createConnection() {
    const sseUrl = `pdSSE/69593048@chnldx.com`;
    this.sseConnection = createSSEConnection(sseUrl, {
      retryInterval: 2000,
      maxRetries: 5,
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });

    this.sseConnection.onMessage((data, event) => {
      this.handleMessage(data, event);
    });

    this.sseConnection.onError((error) => {
      console.error('SSE连接错误:', error);
      this.handleError(error);
    });

    this.sseConnection.onOpen(() => {
      console.log('SSE连接已建立');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.clearReconnectTimer();

      // 连接建立后立即获取一次新消息数量
      this.updateNewMessageCount();
    });

    this.sseConnection.connect();
  }

  /**
   * 处理接收到的消息
   */
  handleMessage(data, event) {
    // 处理新消息气泡数量更新
    this.handleNewMessageBubbleCount(data, event);

    // 通知所有注册的消息处理器
    this.messageHandlers.forEach((handler, key) => {
      try {
        handler(data, event);
      } catch (error) {
        console.error(`SSE消息处理器 ${key} 执行失败:`, error);
      }
    });
  }

  /**
   * 处理 socket 消息（新增方法）
   * @param {Object} socketData socket 消息数据
   */
  handleSocketMessage(socketData) {
    try {
      console.log('sseManager 收到 socket 消息:', socketData);

      // 解析 socket 消息数据
      let data;
      if (typeof socketData === 'string') {
        data = JSON.parse(socketData);
      } else {
        data = socketData;
      }

      // 构造与 SSE 消息格式一致的数据结构
      const messageData = {
        event: 'message',
        data: data,
      };

      console.log('sseManager 处理 socket 消息，构造的数据:', messageData);

      // 处理新消息气泡数量更新
      this.handleNewMessageBubbleCount(messageData, 'socket');

      // 通知所有注册的消息处理器
      this.messageHandlers.forEach((handler, key) => {
        try {
          handler(messageData, 'socket');
        } catch (error) {
          console.error(`Socket消息处理器 ${key} 执行失败:`, error);
        }
      });
    } catch (error) {
      console.error('处理Socket消息失败:', error);
    }
  }

  /**
   * 直接更新未读消息数量（不调用API）
   * @param {number} increment 增加的数量，默认为1
   */
  updateUnreadCountDirectly(increment = 1) {
    try {
      console.log(`直接更新未读消息数量，增加: ${increment}`);

      // 获取当前store中的未读数量
      const currentState = store.state.accountList;
      let currentCount = 0;

      if (currentState.accountList && currentState.accountList.length > 0) {
        currentCount = currentState.accountList[0].unreadCount || 0;
      }

      // 计算新的未读数量
      const newCount = Math.max(0, currentCount + increment);

      console.log(`未读数量更新: ${currentCount} -> ${newCount}`);

      // 直接更新store中的未读消息数量
      store.dispatch('accountList/updateTotalUnreadCount', newCount);
    } catch (error) {
      console.error('直接更新未读消息数量失败:', error);
    }
  }

  /**
   * 处理新消息气泡数量更新
   * 专门用于获取新消息数量并更新气泡显示，不执行其他功能
   */
  handleNewMessageBubbleCount(data, event) {
    try {
      // 检查是否是消息相关的事件
      if (data && data.event) {
        const eventType = data.event;

        // 如果是新消息事件，更新未读数量
        if (eventType === 'newMessage' || eventType === 'message') {
          // 检查消息类型，排除点赞消息
          if (data.data && data.data.messageType) {
            const messageType = data.data.messageType;
            const isLikeMessage =
              messageType.indexOf('like') !== -1 ||
              messageType.indexOf('wink') !== -1;

            // 只处理非点赞消息，用于更新新消息气泡数量
            if (!isLikeMessage) {
              console.log('SSE/Socket检测到新消息，准备更新未读数量');
              // 如果是 socket 消息，直接更新数量；如果是 SSE 消息，调用 API 更新
              if (event === 'socket') {
                this.updateUnreadCountDirectly(1);
              } else {
                this.updateNewMessageCount();
              }
            }
          }
        }
      }

      // 同时检查data.data中是否直接包含消息类型信息
      if (data.data && data.data.messageType) {
        const messageType = data.data.messageType;
        const isLikeMessage =
          messageType.indexOf('like') !== -1 ||
          messageType.indexOf('wink') !== -1;

        // 只处理非点赞消息，用于更新新消息气泡数量
        if (!isLikeMessage) {
          console.log('SSE/Socket检测到新消息数据，准备更新未读数量');
          // 如果是 socket 消息，直接更新数量；如果是 SSE 消息，调用 API 更新
          if (event === 'socket') {
            this.updateUnreadCountDirectly(1);
          } else {
            this.updateNewMessageCount();
          }
        }
      }
    } catch (error) {
      console.error('处理新消息气泡数量更新失败:', error);
    }
  }

  /**
   * 更新新消息数量
   * 调用API获取最新的新消息数量并更新store
   */
  async updateNewMessageCount() {
    // 清除之前的定时器
    if (this.updateCountTimer) {
      clearTimeout(this.updateCountTimer);
    }

    // 设置新的定时器，实现防抖
    this.updateCountTimer = setTimeout(async () => {
      try {
        console.log('开始更新新消息数量...');
        // 动态导入API函数，避免循环依赖
        const { getPdNoReplyChatBoxesPage } = await import('@/api/dialogue');

        // 调用API获取新消息列表（type=0表示新消息）
        const response = await getPdNoReplyChatBoxesPage({
          limit: 100, // 设置一个合理的limit来获取所有新消息
          offset: 0,
          type: 0,
        });

        if (response && response.data) {
          console.log(`获取到新消息数量: ${response.data.length}`);
          // 更新store中的未读消息数量
          store.dispatch(
            'accountList/updateTotalUnreadCount',
            response.data.length
          );
        } else {
          console.log('API返回数据为空，设置未读数量为0');
          store.dispatch('accountList/updateTotalUnreadCount', 0);
        }
      } catch (error) {
        console.error('更新新消息数量失败:', error);
      }
    }, this.updateCountDelay);
  }

  /**
   * 处理连接错误
   */
  handleError(error) {
    this.isConnected = false;
    console.error('SSE连接错误，尝试重连:', error);

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.scheduleReconnect();
    } else {
      console.error('SSE重连次数已达上限，停止重连');
    }
  }

  /**
   * 安排重连
   */
  scheduleReconnect() {
    this.clearReconnectTimer();

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      console.log(
        `SSE重连尝试 ${this.reconnectAttempts}/${this.maxReconnectAttempts}`
      );

      if (this.sseConnection) {
        this.sseConnection.disconnect();
        this.sseConnection = null;
      }

      this.createConnection();
    }, this.reconnectInterval);
  }

  /**
   * 清除重连定时器
   */
  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * 注册消息处理器
   * @param {string} key 处理器标识
   * @param {function} handler 消息处理函数
   */
  registerMessageHandler(key, handler) {
    this.messageHandlers.set(key, handler);
  }

  /**
   * 注销消息处理器
   * @param {string} key 处理器标识
   */
  unregisterMessageHandler(key) {
    this.messageHandlers.delete(key);
  }

  /**
   * 获取连接状态
   */
  getConnectionState() {
    return this.isConnected;
  }

  /**
   * 断开连接
   */
  disconnect() {
    this.clearReconnectTimer();

    // 清理未读数量更新定时器
    if (this.updateCountTimer) {
      clearTimeout(this.updateCountTimer);
      this.updateCountTimer = null;
    }

    if (this.sseConnection) {
      this.sseConnection.disconnect();
      this.sseConnection = null;
    }

    this.isConnected = false;
    this.messageHandlers.clear();
  }

  /**
   * 重新连接
   */
  reconnect() {
    this.reconnectAttempts = 0;
    this.disconnect();
    this.init();
  }
}

// 创建全局单例实例
const sseManager = new SSEManager();

export default sseManager;
